import React, { useEffect, useState } from "react";
import { Icon } from "semantic-ui-react";
import "./ExpandModal.css";

/**
 * Reusable expand popover for Frameworks, Data Sources, and Tabs
 * Positioned relative to trigger element
 * @param {boolean} open - Popover open state
 * @param {function} onClose - Close handler
 * @param {string} title - Popover title (e.g., "APPLIED FRAMEWORKS")
 * @param {JSX.Element} children - Popover content
 * @param {string} position - Position variant: "bottom-left", "bottom-center", "top-right" (default: "bottom-left")
 * @param {HTMLElement} anchorElement - Optional anchor element for dynamic positioning
 */
const ExpandModal = ({ open, onClose, title, children, position = "bottom-left", anchorElement = null }) => {
  const [dynamicStyles, setDynamicStyles] = useState({});

  // Calculate dynamic position based on anchor element
  useEffect(() => {
    if (open && anchorElement) {
      const calculatePosition = () => {
        // Check if anchorElement is a valid DOM element with getBoundingClientRect
        if (!anchorElement || typeof anchorElement.getBoundingClientRect !== 'function') {
          console.warn('ExpandModal: anchorElement is not a valid DOM element');
          setDynamicStyles({});
          return;
        }

        const rect = anchorElement.getBoundingClientRect();
        const modalWidth = 420; // From ExpandModal.css
        const modalMaxHeight = 450; // From ExpandModal.css

        let left = rect.left;
        const gap = 12;
        const spaceBelow = window.innerHeight - rect.bottom - gap;
        const spaceAbove = rect.top - gap;

        // Open above only if not enough space below AND more space above
        const openAbove = spaceBelow < modalMaxHeight && spaceAbove > spaceBelow;

        // Check if modal would overflow right edge
        if (left + modalWidth > window.innerWidth) {
          left = rect.right - modalWidth;
        }

        // Ensure modal doesn't go off left edge
        if (left < 12) {
          left = 12;
        }

        let styles;
        if (openAbove) {
          styles = {
            bottom: `${window.innerHeight - rect.top + gap}px`,
            left: `${left}px`,
            right: 'auto',
            top: 'auto',
            transform: 'none',
            maxHeight: `${Math.min(modalMaxHeight, spaceAbove)}px`
          };
        } else {
          styles = {
            top: `${rect.bottom + gap}px`,
            left: `${left}px`,
            right: 'auto',
            transform: 'none',
            maxHeight: `${Math.min(modalMaxHeight, spaceBelow)}px`
          };
        }

        setDynamicStyles(styles);
      };

      // Calculate on open
      calculatePosition();

      // Recalculate on window resize
      window.addEventListener('resize', calculatePosition);
      return () => window.removeEventListener('resize', calculatePosition);
    } else {
      setDynamicStyles({});  // Clear dynamic styles if no anchor
    }
  }, [open, anchorElement]);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && open) {
        onClose();
      }
    };

    if (open) {
      document.addEventListener("keydown", handleEscape);
      return () => document.removeEventListener("keydown", handleEscape);
    }
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      {/* Backdrop - click to close */}
      <div className="expand-modal-backdrop" onClick={onClose} />

      {/* Popover Panel */}
      <div
        className={`expand-modal-popover expand-modal-${position}`}
        style={anchorElement ? dynamicStyles : {}}
      >
        <div className="expand-modal-header">
          <h3 className="expand-modal-title">{title}</h3>
          <Icon name="close" onClick={onClose} className="expand-modal-close" />
        </div>
        <div className="expand-modal-content">
          {children}
        </div>
      </div>
    </>
  );
};

export default ExpandModal;
